// components/ActiveCycleCard.tsx

import React, { useEffect, useState } from "react";
import { Progress } from "@shared/ui/progress";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Calendar, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { logEvent } from "@shared/analytics/analytics";
import ManageAssignmentDialog from "../ManageAssignmentDialog";
import {
  useAuditUsers,
  usePostMeterCycleAuditUserMapping,
  usePutMeterCycleAuditUserMapping,
} from "@features/mx/validations/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

type CycleData = {
  title: string;
  meterType: string;
  startDate: string;
  endDate: string;
  collectedPercent: number;
  missingPercent: number;
  validatedPercent: number;
  exemptedPercent: number;
  meterCount: number;
  validatorInfo: any[];
  supervisorInfo: any[];
  status: string;
  collectedCount?: number;
  exemptCount?: number;
  missingCount?: number;
  validatedCount?: number;
};

type ActiveCycleCardProps = {
  cycle: any;
  refetchCycle: () => void;
};
export default function ActiveCycleCard({ cycle, refetchCycle }: ActiveCycleCardProps) {
  const navigate = useNavigate();
  const [auditUserSearch, setAuditUserSearch] = useState("");
  const remoteUtilityId = useRemoteUtilityId();
  const [openDialog, setOpenDialog] = useState<
    null | "validator" | "supervisor"
  >(null);

  const userRoleType =
    openDialog === "validator" ? 0 : openDialog === "supervisor" ? 2 : 0; // 0 for validator | 2 for supervisor

  const { data: validatorsList, refetch: refetchValidators  } = useAuditUsers(
    remoteUtilityId,
    userRoleType,
    openDialog === "validator",
    cycle.cycleId,
    auditUserSearch
  );
  const { data: supervisorList, refetch: refetchSupervisors } = useAuditUsers(
    remoteUtilityId,
    userRoleType,
    openDialog === "supervisor",
    cycle.cycleId,
    auditUserSearch
  );

  useEffect(() => {
    if (openDialog === "validator") refetchValidators();
    if (openDialog === "supervisor") refetchSupervisors();
  }, [openDialog]);
  const postMeterCycleAuditUserMappingMutation =
    usePostMeterCycleAuditUserMapping(userRoleType, cycle?.cycleId);
  const putMeterCycleAuditUserMappingMutation =
    usePutMeterCycleAuditUserMapping(userRoleType, cycle?.cycleId);
  const cycleDataArray: CycleData[] = [
    {
      title: cycle?.meterCycleName,
      meterType: cycle?.meterType,
      startDate: cycle?.createdDate,
      endDate: cycle?.validationEnd,
      collectedPercent: cycle?.collectedPercentage,
      missingPercent: cycle?.pendingPercentage,
      validatedPercent: cycle?.validatedPercentage,
      exemptedPercent: cycle?.exemptedPercentage,
      meterCount: cycle?.uniqueMeterCount,
      validatorInfo: cycle?.validatorInfo,
      supervisorInfo: cycle?.supervisorInfo,
      status: cycle?.status,
      collectedCount: cycle?.collectedCount,
      missingCount: cycle?.pendingCount,
      validatedCount: cycle?.validatedCount,
      exemptCount: cycle?.exemptedCount,
    },
  ];

  const handleAssign = (row: any) => {
    const payload = {
      meterCycle: cycle?.cycleId,
      audit_user: row?.id,
      remote_utility_id: remoteUtilityId,
    };
    postMeterCycleAuditUserMappingMutation.mutate(payload, {
      onSuccess: () => {
        logEvent("MX Validator Added", payload);
        refetchCycle?.();
      },
    });
  };

  const handleUnassign = (row: any) => {
    const payload = {
      meter_cycle: cycle?.cycleId,
      audit_user: row?.id,
      remote_utility_id: remoteUtilityId,
    };
    putMeterCycleAuditUserMappingMutation.mutate(payload, {
      onSuccess: () => {
        logEvent("MX Validator Removed", payload);
        refetchCycle?.();
      },
    });
  };

  const handleSave = () => {
    setOpenDialog(null);
  };

  const handleNavigation = (meterType: string) => {
    switch (meterType) {
      case "Photo Meter":
        navigate(`/mx/meter-validation/photometer-validation/${cycle?.runId}`);
        break;

      case "Manual Meter":
        navigate(
          `/mx/meter-validation/manual-meter-validation/${cycle?.runId}`
        );
        break;

      case "Smart Meter":
        toast({
          title: "Info",
          description: "Smart meter is getting ready",
        });
        break;

      default:
        toast({
          title: "Unsupported Type",
          description: "This meter type is not recognized.",
        });
        break;
    }
  };
  return (
    <div>
      {cycleDataArray.map((cycle, index) => (
        <div
          key={index}
          className="rounded-lg border p-4 shadow-sm flex flex-col gap-4"
        >
          {/* Header */}
          <div className="flex justify-start gap-4 items-start">
            <div>
              <h2 className="card-heading-text flex items-center gap-4">
                {cycle.title}{" "}
                <Badge variant="secondary">{cycle.meterType}</Badge>
                <Badge variant="outline">{cycle.status}</Badge>
              </h2>
              <div className="flex items-center text-sm text-muted-foreground mt-1 gap-1">
                <Calendar className="w-4 h-4" />
                <span>
                  {cycle.startDate} - {cycle.endDate}
                </span>
              </div>
            </div>
          </div>

          {/* Progress Bars */}
          <div className="grid grid-cols-2 gap-4">
            {/* Collected */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-sm font-medium">
                <span>Collected</span>
                <span className="text-primary">{cycle.collectedCount}</span>
              </div>
              <Progress value={cycle.collectedPercent} />
            </div>

            {/* Missing */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-sm font-medium">
                <span>Missing</span>
                <span className="text-yellow-500">{cycle.missingCount}</span>
              </div>
              <Progress
                value={cycle.missingPercent}
                className="bg-yellow-100"
              />
            </div>

            {/* Validated */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-sm font-medium">
                <span>Validated</span>
                <span className="text-green-500">
                  {cycle.validatedCount}
                </span>
              </div>
              <Progress
                value={cycle.validatedPercent}
                className="bg-green-100"
              />
            </div>

            {/* Exempted */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-sm font-medium">
                <span>Exempted</span>
                <span className="text-rose-500">{cycle.exemptCount}</span>
              </div>
              <Progress value={cycle.exemptedPercent} className="bg-rose-100" />
            </div>
          </div>

          {/* Footer Info */}
          <div className="flex justify-between text-sm text-muted-foreground mt-2">
            <div>
              <div className="card-subheading-text">Meter Count</div>
              <div className="text-foreground font-semibold">
                {cycle.meterCount}
              </div>
            </div>
            <div>
              <div>Validator</div>
              <div className="text-foreground font-semibold flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer flex items-center gap-3">
                      {Array.isArray(cycle.validatorInfo)
                        ? cycle.validatorInfo.length
                        : 0}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {Array.isArray(cycle.validatorInfo) &&
                    cycle.validatorInfo.length > 0
                      ? cycle.validatorInfo
                          .map((v) => v.validatorName)
                          .join(", ")
                      : "No validators"}
                  </TooltipContent>
                </Tooltip>
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenDialog("validator");
                  }}
                >
                  <Settings size={18} />
                </Button>
              </div>
            </div>
            <div>
              <div>Supervisor</div>
              <div className="text-foreground font-semibold flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="cursor-pointer">
                      {Array.isArray(cycle.supervisorInfo)
                        ? cycle.supervisorInfo.length
                        : 0}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {Array.isArray(cycle.supervisorInfo) &&
                    cycle.supervisorInfo.length > 0
                      ? cycle.supervisorInfo
                          .map((v) => v.supervisorName)
                          .join(", ")
                      : "No Supervisor"}
                  </TooltipContent>
                </Tooltip>
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenDialog("supervisor");
                  }}
                >
                  <Settings size={18} />
                </Button>
              </div>
            </div>
          </div>

          {/* CTA */}
          <Button
            className="w-full mt-2"
            onClick={() => {
              handleNavigation(cycle.meterType);
            }}
          >
            View Cycle
          </Button>
        </div>
      ))}
      {openDialog && (
        <ManageAssignmentDialog
          open={!!openDialog}
          onOpenChange={(open) => !open && setOpenDialog(null)}
          type={openDialog}
          data={openDialog === "validator" ? validatorsList : supervisorList}
          onAssign={handleAssign}
          onUnassign={handleUnassign}
          onSearch={(search) => {
            setAuditUserSearch(search);
          }}
        />
      )}
    </div>
  );
}
